import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BeakerIcon, 
  PlusIcon, 
  TrashIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface Variant {
  name: string;
  description: string;
  trafficAllocation: number;
}

const ABTestCreator: React.FC = () => {
  const navigate = useNavigate();
  const [creatorProfileId, setCreatorProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [testType, setTestType] = useState<'SIMPLE' | 'MULTIVARIATE'>('SIMPLE');
  const [targetMetric, setTargetMetric] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minSampleSize, setMinSampleSize] = useState(100);
  const [confidenceLevel, setConfidenceLevel] = useState(0.95);
  const [variants, setVariants] = useState<Variant[]>([
    { name: 'Control', description: 'Original version', trafficAllocation: 50 },
    { name: 'Variant A', description: 'Test variation', trafficAllocation: 50 },
  ]);

  useEffect(() => {
    fetchCreatorProfile();
  }, []);

  const fetchCreatorProfile = async () => {
    try {
      const response = await axios.get('/api/users/me');
      const userId = response.data.id;
      const profileResponse = await axios.get(`/api/creators/${userId}`);
      setCreatorProfileId(profileResponse.data.id);
    } catch (err) {
      console.error('Error fetching creator profile:', err);
      setError('Failed to load creator profile');
    }
  };

  const addVariant = () => {
    const newVariant: Variant = {
      name: `Variant ${String.fromCharCode(65 + variants.length - 1)}`,
      description: '',
      trafficAllocation: 0,
    };
    setVariants([...variants, newVariant]);
    redistributeTraffic([...variants, newVariant]);
  };

  const removeVariant = (index: number) => {
    if (variants.length <= 2) {
      alert('You must have at least 2 variants');
      return;
    }
    const newVariants = variants.filter((_, i) => i !== index);
    redistributeTraffic(newVariants);
  };

  const redistributeTraffic = (variantList: Variant[]) => {
    const equalAllocation = Math.floor(100 / variantList.length);
    const remainder = 100 - (equalAllocation * variantList.length);
    
    const newVariants = variantList.map((v, i) => ({
      ...v,
      trafficAllocation: equalAllocation + (i === 0 ? remainder : 0),
    }));
    
    setVariants(newVariants);
  };

  const updateVariant = (index: number, field: keyof Variant, value: string | number) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setVariants(newVariants);
  };

  const handleTrafficChange = (index: number, value: number) => {
    const newVariants = [...variants];
    newVariants[index].trafficAllocation = value;
    setVariants(newVariants);
  };

  const getTotalTraffic = () => {
    return variants.reduce((sum, v) => sum + v.trafficAllocation, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const totalTraffic = getTotalTraffic();
    if (totalTraffic !== 100) {
      alert(`Traffic allocation must sum to 100% (currently ${totalTraffic}%)`);
      return;
    }

    if (!creatorProfileId) {
      setError('Creator profile not loaded');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const testData = {
        name,
        description,
        creatorProfileId,
        testType,
        targetMetric,
        variants,
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        minSampleSize,
        confidenceLevel,
      };

      await axios.post('/api/ab-testing', testData);
      
      alert('Test created successfully!');
      navigate('/dashboard/ab-testing');
    } catch (err: any) {
      console.error('Error creating test:', err);
      setError(err.response?.data?.error || 'Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <BeakerIcon className="h-8 w-8 text-blue-600" />
          Create A/B Test
        </h1>
        <p className="mt-2 text-gray-600">
          Set up an experiment to test different versions of your content
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Tier Price Optimization"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="What are you testing and why?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Type *
              </label>
              <select
                value={testType}
                onChange={(e) => setTestType(e.target.value as 'SIMPLE' | 'MULTIVARIATE')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="SIMPLE">Simple (A/B)</option>
                <option value="MULTIVARIATE">Multivariate</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Metric *
              </label>
              <input
                type="text"
                value={targetMetric}
                onChange={(e) => setTargetMetric(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., subscription_conversion"
              />
            </div>
          </div>
        </div>

        {/* Variants */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Variants</h2>
            <button
              type="button"
              onClick={addVariant}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add Variant
            </button>
          </div>

          <div className="space-y-4">
            {variants.map((variant, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Variant {index + 1}</h3>
                  {variants.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeVariant(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={variant.name}
                      onChange={(e) => updateVariant(index, 'name', e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={variant.description}
                      onChange={(e) => updateVariant(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Traffic Allocation: {variant.trafficAllocation}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={variant.trafficAllocation}
                      onChange={(e) => handleTrafficChange(index, parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={`flex items-center gap-2 p-3 rounded-lg ${
            getTotalTraffic() === 100 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <InformationCircleIcon className={`h-5 w-5 ${
              getTotalTraffic() === 100 ? 'text-green-600' : 'text-yellow-600'
            }`} />
            <span className={`text-sm ${
              getTotalTraffic() === 100 ? 'text-green-800' : 'text-yellow-800'
            }`}>
              Total traffic allocation: {getTotalTraffic()}% {getTotalTraffic() !== 100 && '(must be 100%)'}
            </span>
          </div>
        </div>

        {/* Schedule & Settings */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Schedule & Settings</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date (optional)
              </label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date (optional)
              </label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Sample Size
              </label>
              <input
                type="number"
                value={minSampleSize}
                onChange={(e) => setMinSampleSize(parseInt(e.target.value))}
                min="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confidence Level
              </label>
              <select
                value={confidenceLevel}
                onChange={(e) => setConfidenceLevel(parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="0.90">90%</option>
                <option value="0.95">95%</option>
                <option value="0.99">99%</option>
              </select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/ab-testing')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || getTotalTraffic() !== 100}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Test'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ABTestCreator;
